import { Stack, Typography } from "@mui/material";

import React, { useState } from "react";
import CaptionAndFilter from "./component/CaptionAndFilter";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { useParams } from "react-router-dom";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { VITAL_SIGNS_GRAPH } from "utils/reactQueryKeys";
import { useSanitizeQueryParams } from "utils/sanitizeQuery";
import CustomLoader from "components/atoms/CustomLoader";
import CustomGraphTooltip from "./component/CustomGraphTooltip";
import CustomRefLine from "./component/CustomRefLine";

const Graph = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data?.graph ?? []}>
        <defs>
          <linearGradient id="gradientFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="10%" stopColor="rgba(251, 175, 0, 0.4)" />
            <stop offset="100%" stopColor="rgba(251, 175, 0, 0)" />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="xName" />
        <YAxis
          unit={data?.range?.unit}
          domain={["auto", Number(data?.range?.normal?.max || 0) + 5]}
          allowDecimals
          tickCount={10}
          width={80}
        />
        <Tooltip content={<CustomGraphTooltip yName="Glucose Level" />} />

        <ReferenceLine
          y={Number(data?.range?.normal?.max ?? 0)}
          label={
            <CustomRefLine
              range={`${data?.range?.normal?.min}${data?.range?.unit} - ${data?.range?.normal?.max}${data?.range?.unit}`}
            />
          }
          stroke="#00843566"
          strokeDasharray="5 5"
        />
        <Area
          type="monotone"
          dataKey="yValue"
          unit={data?.range?.unit}
          stroke="#FBAF00"
          strokeWidth={"3px"}
          fill="url(#gradientFill)"
          dot={false}
        />
        <ReferenceLine
          y={Number(data?.range?.normal?.min ?? 0)}
          label=""
          stroke="#00843566"
          strokeDasharray="5 5"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const VITALS_CHECKED = "glucose";

function GlucoseLevel() {
  const { id } = useParams();

  const [filter, setfilter] = useState({
    name: "This Month",
    value: "ThisMonth",
  });

  //get vital signs
  const {
    data: vitalsGraph,
    isLoading,
    isError,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleDropdown = (item) => {
    setfilter(item);
  };
  return (
    <Stack
      spacing={4}
      sx={{
        border: {
          lg: "1px solid rgba(196, 196, 196, 0.25)",
        },
        padding: {
          xs: 0,
          lg: 3,
        },
        borderRadius: {
          xs: "none",
          lg: "12px",
        },
      }}
    >
      <CaptionAndFilter
        filter={filter}
        handleDropdown={handleDropdown}
        icon={
          <svg
            width="20"
            height="29"
            viewBox="0 0 20 29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.7924 23.6264L14.6863 21.3782V16.8819L10.7924 19.13V23.6264ZM6.07812 15.461L9.97204 17.7092L13.866 15.461L9.97204 13.2129L6.07812 15.461ZM5.25781 21.3782L9.15173 23.6264V19.13L5.25781 16.8819V21.3782Z"
              fill="#FBAF00"
            />
            <path
              d="M18.1904 13.1962C16.3143 10.3413 10.0019 0.5 10.0019 0.5C10.0019 0.5 2.06195 12.8123 1.80968 13.1962C0.72801 14.8423 0.15625 16.7307 0.15625 18.6572C0.15625 24.0845 4.57217 28.4999 10 28.4999C15.4279 28.4999 19.8438 24.0845 19.8438 18.6572C19.8438 16.7307 19.2721 14.8423 18.1904 13.1962ZM16.3276 22.3252L9.97269 25.9942L3.61782 22.3252V14.9872L9.97269 11.3182L16.3276 14.9872V22.3252Z"
              fill="#FBAF00"
            />
          </svg>
        }
        title={"Glucose Level"}
        subTitle={`Blood glucose level measured in ${
          !isLoading && !isError && vitalsGraph?.data
            ? vitalsGraph?.data?.range?.unit
            : "mg/dL."
        } `}
      />
      <Stack
        sx={{
          width: {
            xs: "70vw",
            sm: "50vw",
          },
          height: {
            xs: "35vh",
            sm: "40vh",
            md: "70vh",
          },
        }}
      >
        {isLoading ? (
          <CustomLoader />
        ) : !isError ? (
          <Graph data={vitalsGraph.data} />
        ) : (
          "No data to display"
        )}
        {!isLoading && !isError && (
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.name}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default GlucoseLevel;
